import React, {
  ChangeEvent,
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import AutosizeInput from "react-input-autosize";
import Select from "react-select";

import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";

import styles from "./CommonCell.module.scss";

export function CommonCell({
  children,
  title = "",
  style = {},
}: {
  children: ReactNode;
  title?: string;
  style?: CSSProperties;
}) {
  const tooltipRef = useRef(null);

  return (
    <>
      <div
        ref={tooltipRef}
        data-pr-tooltip={title || ""} // Tooltip text
        data-pr-position={"bottom"}
        className={styles.commonCell}
        style={style}
      >
        <div>{children}</div>
      </div>

      <Tooltip target={tooltipRef} />
    </>
  );
}
export function CellInput({
  defaultValue = "",
  value,
  name = "",
  type = "text",
  style = {},
  disabled = false,
  extension,
  min,
  max,
  onChange,
}: {
  defaultValue?: string;
  name?: string;
  type?: string;
  style?: CSSProperties;
  extension?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  value?: string | number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);

  const [tooltip, setTooltip] = useState(value || inputRef?.current?.value);

  useEffect(() => {
    setTooltip(value || inputRef?.current?.value);
  }, [value, inputRef]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTooltip(e.target.value);
    onChange?.(e);
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={onChange ? styles.autosizeInputContainer : styles.commonCell}
      style={{
        ...style,
        backgroundColor: focus ? "var(--color-primary-opacity-2)" : "white",
      }}
    >
      {onChange ? (
        <>
          <AutosizeInput
            data-pr-tooltip={`${tooltip}`}
            onBlur={() => setFocus(false)}
            onChange={onChange}
            onFocus={() => setFocus(true)}
            ref={inputRef as any}
            extraWidth={0}
            inputClassName={styles.autosizeInput}
            inputStyle={{
              border: "none",
              padding: "0 0 0 0",
              height: "100%",
              outline: "none",
            }}
            step="0.01"
            disabled={disabled}
            name={name}
            type={type}
            {...(typeof value !== "undefined" ? { value } : { defaultValue })}
            {...(typeof min !== "undefined" && { min })}
            {...(typeof max !== "undefined" && { max })}
          />
          {extension && <div className={styles.extension}>{extension}</div>}
        </>
      ) : (
        <InputText
          id={name}
          tooltip={`${tooltip}`}
          tooltipOptions={{ position: "bottom" }}
          name={name}
          ref={inputRef}
          onChange={handleChange}
          defaultValue={defaultValue}
          {...(typeof min !== "undefined" && { min })}
          {...(typeof max !== "undefined" && { max })}
        />
      )}
    </div>
  );
}

export function CellSelect({
  defaultValue = "",
  name = "",
  options = [],
  value,
  placeholder,
  onChange,
  tooltipText,
}: {
  defaultValue: string;
  name?: string;
  placeholder: string;
  onChange: (option: any) => void;
  options: any;
  value: any;
  tooltipText: string;
}) {
  const selectRef = useRef(null);

  return (
    <>
      <span
        ref={selectRef}
        data-pr-tooltip={tooltipText}
        data-pr-position="bottom"
      >
        <Select
          id={name}
          placeholder={placeholder}
          value={value}
          onChange={(option) => onChange(option)}
          styles={{
            menu: (base) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
            control: (base) => ({
              ...base,
              height: 36,
              minHeight: 36,
              width: 200,
            }),
          }}
          menuPortalTarget={document.querySelector("body")}
          name={name}
          defaultValue={options.find(
            (option: any) => option.value === defaultValue,
          )}
          options={options}
        />
      </span>

      <Tooltip target={selectRef} />
    </>
  );
}
